<template>
  <v-menu
    v-model="isHelpOpened"
    :close-on-content-click="false"
    location="top"
  >
    <template v-slot:activator="{ props }">
      <div 
        v-bind="props"
        data-locator="ai-assistant-help-button"
        class="d-flex align-center pa-1 rounded cursor-pointer column-gap-1 help-button"
        :class="{ 'active': isHelpOpened }"
      >
        <app-material-icon name="info" :size="16" />
        <p>{{ t('helpButtonLabel') }}</p>
      </div>
    </template>

    <v-card class="help-card d-flex flex-column pa-4 row-gap-4">
      <div>
        <div class="d-flex justify-space-between align-center">
          <div class="font-weight-bold help-title">{{ t('helpCardTitle') }}</div>
          <app-button type="icon" size="x-small" @click="isHelpOpened = false">
            <app-material-icon name="close" :size="14"/>
          </app-button>
        </div>
        <p>{{ t('helpCardSubTitle') }}</p>
      </div>
      <div class="d-flex flex-column row-gap-2">
        <span class="help-description">{{ t('actifTitle') }}</span>
        <div v-for="block in actifContent" :key="block.id">
          <div>
            <span :class="block.colorClass" class="font-weight-bold">
              {{ block.title }}{{ block.required ? '*' : '' }}
            </span>
          </div>
          <p>{{ block.description }}</p>
        </div>
      </div>
      <div class="d-flex flex-column row-gap-2">
        <p>{{ t('exampleTitle') }}</p>
        <div>
          <template v-for="block in actifContent" :key="block.id">
            <span :class="block.blockClass" class="rounded text-block">
              <span class="font-weight-bold">{{ block.title }} : </span>
              <span>{{ block.example }}</span>
            </span>
          </template>
        </div>
        <p>{{ finalMessage }}</p>
      </div>

      <v-divider opacity="1" color="#CED6DE"/>
      
      <div class="d-flex justify-end">
        <app-button size="small" type="ghost" class="close-button font-weight-bold" @click="isHelpOpened = false">
          {{ tCommon('close') }}
        </app-button>
      </div>
    </v-card>
  </v-menu>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'

import { DARK_BLUE_DARKEST } from '@/common/utils/colors'
import { useTranslation } from '@/common/utils/translation'

import type { Ref, ComputedRef } from 'vue'

interface ActifItem {
  id: string
  color: string
  block: string
  required: boolean
}

interface ActifContent {
  id: string
  title: string
  description: string
  example: string
  colorClass: string
  blockClass: string
  required: boolean
}

const { t } = useTranslation({ keyPrefix: 'common.ai-assistant.AIAssistantPaneHelp' })
const { t: tCommon } = useTranslation({ keyPrefix: 'common' })

const isHelpOpened: Ref<boolean> = ref(false)

const finalMessage: ComputedRef<string> = computed(() => t('finalMessage'))

const actifContent: ComputedRef<ActifContent[]> = computed(() => {
  const items: ActifItem[] = [
    { id: 'action', color: 'text--pink', block: 'block-pink', required: true },
    { id: 'context', color: 'text--orange', block: 'block-orange', required: true },
    { id: 'tone', color: 'text--green', block: 'block-green', required: false },
    { id: 'identity', color: 'text--blue', block: 'block-blue', required: false },
    { id: 'format', color: 'text--amber', block: 'block-amber', required: false },
  ]
  
  return items.map((item: ActifItem): ActifContent => ({
    id: item.id,
    title: t(`actifContent.${item.id}.title`),
    description: t(`actifContent.${item.id}.description`),
    example: t(`actifContent.${item.id}.example`),
    colorClass: item.color,
    blockClass: item.block,
    required: item.required,
  }))
})
</script>

<style scoped lang="scss">
.help-card {
  font-family: 'Lato', sans-serif;
  border: 2px solid;
  border-color: #A689ED;
  border-radius: 4px;
  max-width: 350px;
  background: linear-gradient(305.19deg, #FFFFFF 73.71%, #F1EBFC 100.31%) !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15) !important;
  margin-bottom: 4px;
}
p {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
}
p, .help-description {
  color: #2A476A;
}
.help-description {
  line-height: 20px;
}
.help-title {
  color: v-bind(DARK_BLUE_DARKEST);
  line-height: 24px;
  font-size: 16px;
}
.help-button {
  &:hover,
  &.active {
    background-color: #ECEFF1;
  }
  transition: background-color 0.3s ease;
}
.close-button {
  border: 2px solid #00172E;
  border-radius: 4px;
}
.text-block {
  margin-right: 1px;
  padding: 0px 2px;
  color: #103051;
}
.block-pink {
  background-color: #F9A8CA;
}

.block-orange {
  background-color: #FFD49E;
}

.block-green {
  background-color: #C6F1CB;
}

.block-blue {
  background-color: #CAE0FC;
}

.block-amber {
  background-color: #FFE7AD;
}

.text--pink {
  color: #EB4683;
}

.text--orange {
  color: #FF8B29;
}

.text--green {
  color: #6FB024;
}

.text--blue {
  color: #3A7CEE;
}

.text--amber {
  color: #F2B21D;
}
</style>